import { chatApi } from "../../../services/api";
import { Bubble, Prompts, Sender, useXAgent, useXChat } from "@ant-design/x";
import type { BubbleProps } from "@ant-design/x";
import {
  GetProp,
  Upload,
  Modal,
  message as antMessage,
  Button,
} from "antd/lib";
import React, { useEffect, useRef, useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import aiAvatar from "../../../assets/ai-avatar.png";
import userAvatar from "../../../assets/user-avatar.png";
import {
  UserOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  PlusOutlined,
  PoweroffOutlined,
  BulbOutlined,
  MoonOutlined,
  ExportOutlined,
  DownloadOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useStyle } from "../../../styles/uchat/UCIndependentChat.styles";
import { renderLocalMessage } from "../../ChatUtils";
import { Flex } from "antd";
import QuickQuestionFloating from "../UCQuickQuestionFloating";

// 导入类型和常量
import type { IndependentProps } from "./types";
import { senderPromptsItems } from "./constants";

// 导入hooks
import { useChatState } from "./hooks/useChatState";
import { useFileHandlers } from "./hooks/useFileHandlers";
import { useMessageHandlers } from "./hooks/useMessageHandlers";
import { useEvaluationHandlers } from "./hooks/useEvaluationHandlers";
import { useQuickQuestionHandlers } from "./hooks/useQuickQuestionHandlers";
import { useRobotSettings } from "./hooks/useRobotSettings";
import { usePreviewHandlers } from "./hooks/usePreviewHandlers";
import { useTheme } from "./hooks/useTheme";
import { usePageTheme } from "../v2/theme/usePageTheme";
import {
  quickQuestionThemeFromLegacyColors,
  quickQuestionThemeFromPageTokens,
} from "../quickQuestionTheme";
import { useMarkReadWhenViewed } from "./hooks/useMarkReadWhenViewed";
import V2AttachmentIcon from "../v2/icons/V2AttachmentIcon";
import V2CloseSessionButton from "../v2/icons/V2CloseSessionButton";
import V2SendIcon from "../v2/icons/V2SendIcon";
import { openV2ConfirmModal } from "../v2/openV2ConfirmModal";

// 导入组件
import { ImagePreview } from "./components/ImagePreview";
import { VideoPreview } from "./components/VideoPreview";
import { FilePreview } from "./components/FilePreview";

const IndependentChat: React.FC<IndependentProps> = ({
  csUuid,
  code,
  uid,
  onRef,
  status,
  themeColors: externalThemeColors,
  isDark: externalIsDark,
  quickQaIds,
  uiVariant = "legacy",
  onSessionClosed,
}) => {
  console.log("csUuid", csUuid);
  console.log("code", code);
  console.log("status", status);
  // ==================== Theme ====================
  // 如果外部提供了主题，使用外部主题；否则使用内部主题管理
  const internalTheme = useTheme();
  const colors = externalThemeColors || internalTheme.colors;
  const isDark =
    externalIsDark !== undefined ? externalIsDark : internalTheme.isDark;
  const intl = useIntl();
  const { tokens: v2Tokens } = usePageTheme();

  const v2UserBubbleStyle = useMemo(
    () =>
      uiVariant === "v2"
        ? {
            background: `linear-gradient(180deg, ${v2Tokens.chatUserGradientStart} 0%, ${v2Tokens.chatUserGradientEnd} 100%)`,
            backgroundColor: v2Tokens.chatUserGradientStart,
            color: v2Tokens.chatUserMessageText,
          }
        : undefined,
    [uiVariant, v2Tokens]
  );

  const v2AgentBubbleStyle = useMemo(
    () =>
      uiVariant === "v2"
        ? {
            backgroundColor: v2Tokens.chatAgentBubbleBg,
            color: v2Tokens.chatMessageText,
            border: `1px solid ${v2Tokens.chatAgentBubbleBorder}`,
          }
        : undefined,
    [uiVariant, v2Tokens]
  );

  // 使用 useMemo 来稳定 toggleTheme 引用
  const toggleTheme = useMemo(() => {
    return externalThemeColors ? () => {} : internalTheme.toggleTheme;
  }, [externalThemeColors, internalTheme.toggleTheme]);

  // ==================== Style ====================
  const { styles } = useStyle(colors);

  // ==================== State ====================
  const chatState = useChatState(csUuid, status);
  const {
    content,
    setContent,
    messages,
    setMessages,
    previewVisible,
    setPreviewVisible,
    previewImage,
    setPreviewImage,
    csUuidRef,
    imageLoading,
    setImageLoading,
    previewBase64,
    setPreviewBase64,
    uploadedImageUrl,
    setUploadedImageUrl,
    uploadedImageUrlRef,
    videoLoading,
    setVideoLoading,
    previewVideo,
    setPreviewVideo,
    uploadedVideoUrl,
    setUploadedVideoUrl,
    uploadedVideoUrlRef,
    videoVisible,
    setVideoVisible,
    currentVideo,
    setCurrentVideo,
    fileLoading,
    setFileLoading,
    previewFile,
    setPreviewFile,
    uploadedFileUrl,
    setUploadedFileUrl,
    uploadedFileUrlRef,
    fileVisible,
    setFileVisible,
    currentFile,
    setCurrentFile,
    chatStatus,
    setChatStatus,
    messageRatings,
    setMessageRatings,
    fileInputRef,
    quickQuestions,
    setQuickQuestions,
    robotSettings,
    setRobotSettings,
    smartCompressor,
  } = chatState;

  // 创建 contentRef 用于在文件上传时获取当前内容
  const contentRef = useRef(content);
  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  // ==================== Robot Settings ====================
  useRobotSettings({
    code,
    setQuickQuestions,
    setRobotSettings,
  });

  // ==================== File Handlers ====================
  const {
    clearSelectedMedia,
    handleFileSelect,
    handleImageUpload,
    handleVideoUpload,
    handleGeneralFileUpload,
  } = useFileHandlers({
    smartCompressor,
    setImageLoading,
    setPreviewBase64,
    setUploadedImageUrl,
    uploadedImageUrlRef,
    setVideoLoading,
    setPreviewVideo,
    setUploadedVideoUrl,
    uploadedVideoUrlRef,
    setFileLoading,
    setPreviewFile,
    setUploadedFileUrl,
    uploadedFileUrlRef,
    setContent,
    contentRef,
    fileInputRef,
  });

  // ==================== Evaluation Handlers ====================
  const { handleEvaluationSubmit, handleEvaluationSkip } =
    useEvaluationHandlers({
      csUuidRef,
      setMessages,
      styles,
    });

  // ==================== Agent Typing（客服正在输入）====================
  const [showAgentTyping, setShowAgentTyping] = useState(false);
  const showAgentTypingRef = useRef(false);
  /** 发消息后等待回复：只加速轮询，不立刻展示 typing */
  const awaitingReplyRef = useRef(false);
  const awaitingStartedAtRef = useRef(0);
  /** 未配置时默认开启；明确为 false 时关闭 */
  const typingHintEnabled = robotSettings.switchTypingHint !== false;

  useEffect(() => {
    showAgentTypingRef.current = showAgentTyping;
  }, [showAgentTyping]);

  // 开关关闭时强制隐藏提示
  useEffect(() => {
    if (!typingHintEnabled) {
      setShowAgentTyping(false);
    }
  }, [typingHintEnabled]);

  const handleAwaitReplyStart = useCallback(
    (opts?: { minReplyAfterSec?: number }) => {
      awaitingReplyRef.current = true;
      const now = Math.floor(Date.now() / 1000);
      // 快捷问题预设 BOT 与点击同秒写入，跟进检测须晚于预设时间，避免误判为 Agent 已回复
      if (opts?.minReplyAfterSec != null && opts.minReplyAfterSec > 0) {
        awaitingStartedAtRef.current = Math.max(now, opts.minReplyAfterSec + 1);
      } else {
        awaitingStartedAtRef.current = now;
      }
    },
    []
  );

  const handleTypingSync = useCallback(
    (payload: {
      serverTyping: boolean;
      hasPendingReply: boolean;
      hasNewAgentReply: boolean;
    }) => {
      // 开关关闭：不展示
      if (robotSettings.switchTypingHint === false) {
        setShowAgentTyping(false);
        return;
      }

      const activeSignal = payload.serverTyping || payload.hasPendingReply;

      // 服务端 typing / 空 BOT·answering：立即展示，并 sticky 住
      if (activeSignal) {
        setShowAgentTyping(true);
      }

      // 正式回复已写入消息列表后才关闭（避免 Redis/pending 先清、气泡尚未渲染）
      if (payload.hasNewAgentReply) {
        setShowAgentTyping(false);
        awaitingReplyRef.current = false;
        awaitingStartedAtRef.current = 0;
        return;
      }

      // 未在等待「发消息后的回复」：跟人工业务信号（输入框清空等可直接关）
      if (!awaitingReplyRef.current && !activeSignal) {
        setShowAgentTyping(false);
      }

      // 等待超时 90s：停止加速并关掉 sticky，避免空转
      if (
        awaitingReplyRef.current &&
        awaitingStartedAtRef.current > 0 &&
        Math.floor(Date.now() / 1000) - awaitingStartedAtRef.current > 90
      ) {
        awaitingReplyRef.current = false;
        awaitingStartedAtRef.current = 0;
        setShowAgentTyping(false);
      }
    },
    [robotSettings.switchTypingHint]
  );

  // ==================== Message Handlers ====================
  const { createQaRecord, fetchChatContent, handleRateMessage } =
    useMessageHandlers({
      csUuidRef,
      setMessages,
      setChatStatus: setChatStatus as (status: string) => void,
      messageRatings,
      setMessageRatings,
      handleEvaluationSubmit,
      handleEvaluationSkip,
      styles,
      intl,
      themeColors: colors,
      uiVariant,
      onAwaitReplyStart: handleAwaitReplyStart,
      awaitingStartedAtRef,
      onTypingSync: handleTypingSync,
    });

  // ==================== Quick Question Handlers ====================
  const { handleQuickQuestionClick } = useQuickQuestionHandlers({
    csUuidRef,
    setMessages,
    messageRatings,
    handleRateMessage,
    styles,
    uiVariant,
    onAwaitReplyStart: handleAwaitReplyStart,
  });

  // ==================== Preview Handlers ====================
  const {
    handlePreview,
    handleCancel,
    handleVideoPreview,
    handleVideoCancel,
    handleFilePreview,
    handleFileCancel,
  } = usePreviewHandlers({
    setPreviewVisible,
    setPreviewImage,
    setVideoVisible,
    setCurrentVideo,
    setFileVisible,
    setCurrentFile,
  });

  // ==================== 智能体 ====================
  const [agent] = useXAgent<string>({
    request: async (info, callbacks) => {
      try {
        const imageUrl = uploadedImageUrlRef.current;
        const videoUrl = uploadedVideoUrlRef.current;
        const fileUrl = uploadedFileUrlRef.current;
        const rawMessage = (info as any)?.message as string | undefined;
        const message =
          rawMessage && rawMessage.trim() !== "" ? rawMessage.trim() : null;

        const result = await createQaRecord(
          message,
          imageUrl,
          videoUrl,
          fileUrl
        );
        if (result) {
          callbacks.onSuccess([
            { data: intl.formatMessage({ id: "message.send.success" }) },
          ] as any);
        } else {
          callbacks.onError(
            new Error(intl.formatMessage({ id: "message.send.error" }))
          );
          antMessage.error(intl.formatMessage({ id: "message.send.error" }));
        }
      } catch (error) {
        console.error("Error in request:", error);
        callbacks.onError(error as Error);
        antMessage.error(intl.formatMessage({ id: "message.send.error" }));
      }
    },
  });

  const { onRequest } = useXChat({
    agent,
  });

  // ==================== Fetch Chat Content ====================
  // 使用 ref 保存最新的 chatStatus，避免定时器闭包问题
  const chatStatusRef = useRef(chatStatus);
  useEffect(() => {
    chatStatusRef.current = chatStatus;
  }, [chatStatus]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const scheduleNext = () => {
      if (cancelled) return;
      // 等待回复或正在输入时加速轮询，否则 10s
      const interval =
        showAgentTypingRef.current || awaitingReplyRef.current ? 2500 : 10000;
      timer = setTimeout(runPoll, interval);
    };

    const runPoll = async () => {
      if (cancelled) return;
      const currentStatus = chatStatusRef.current;
      // 已关闭：停止轮询
      if (currentStatus === "close") {
        return;
      }
      if (currentStatus === "undistributed" || currentStatus === "chatting") {
        try {
          await fetchChatContent();
        } catch {
          // 轮询失败不影响下一轮
        }
      } else {
        return;
      }
      scheduleNext();
    };

    // 初始加载后进入轮询
    (async () => {
      await fetchChatContent();
      scheduleNext();
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [fetchChatContent, chatStatus]);

  // ==================== 关闭聊天功能 ====================
  const handleCloseChat = useCallback(async () => {
    try {
      // 调用后端接口关闭聊天会话
      const response = await chatApi.closeChat({
        csUuid: csUuidRef.current,
      });

      if (response.success) {
        clearSelectedMedia();
        setContent("");
        setChatStatus("close");
        fetchChatContent();

        if (uiVariant === "v2" && onSessionClosed) {
          onSessionClosed();
          if (!robotSettings.switchChatEvaluate) {
            antMessage.success(
              intl.formatMessage({ id: "close.chat.success" })
            );
          }
        } else {
          antMessage.success(intl.formatMessage({ id: "close.chat.success" }));
        }
      } else {
        antMessage.error(intl.formatMessage({ id: "close.chat.error" }));
      }
    } catch (error) {
      antMessage.error(intl.formatMessage({ id: "close.chat.error" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clearSelectedMedia,
    intl,
    robotSettings.switchChatEvaluate,
    uiVariant,
    onSessionClosed,
    setChatStatus,
    fetchChatContent,
  ]);

  // ==================== UI 状态控制 ====================
  // 获取输入框状态
  const getSenderProps = useCallback(() => {
    switch (chatStatus) {
      case "close":
        return {
          disabled: false,
          placeholder: intl.formatMessage({ id: "input.placeholder.closed" }),
        };
      default:
        return {
          disabled: false,
          placeholder: intl.formatMessage({ id: "input.placeholder.chatting" }),
        };
    }
  }, [chatStatus, intl]);

  const openCloseChatConfirm = useCallback(() => {
    if (chatStatus === "close") {
      antMessage.error(
        intl.formatMessage({ id: "close.chat.already.closed" })
      );
      return;
    }
    if (imageLoading || videoLoading || fileLoading) return;

    const title = intl.formatMessage({ id: "close.chat.confirm.title" });
    const confirmContent = intl.formatMessage({
      id: "close.chat.confirm.content",
    });
    const okText = intl.formatMessage({ id: "close.chat.confirm.ok" });
    const cancelText = intl.formatMessage({
      id: "close.chat.confirm.cancel",
    });

    if (uiVariant === "v2") {
      openV2ConfirmModal({
        tokens: v2Tokens,
        title,
        content: confirmContent,
        okText,
        cancelText,
        onOk: handleCloseChat,
      });
      return;
    }

    Modal.confirm({
      title,
      content: confirmContent,
      okText,
      cancelText,
      onOk: async () => {
        await handleCloseChat();
      },
    });
  }, [
    chatStatus,
    imageLoading,
    videoLoading,
    fileLoading,
    handleCloseChat,
    intl,
    uiVariant,
    v2Tokens,
  ]);

  const shouldShowCloseButton =
    chatStatus !== "close" && !!robotSettings.switchChatEvaluate;

  // 创建 Sender.Header 组件
  const headerNode = useMemo(() => {
    if (!shouldShowCloseButton) return null;

    return (
      <Sender.Header
        open
        styles={{ header: { background: colors.background } }}
        closable={false}
      >
        <div className={styles.headerCloseWrapper}>
          {/* 主题切换按钮 - 仅在内部管理主题时显示 */}
          {!externalThemeColors && (
            <div
              onClick={toggleTheme}
              role="button"
              aria-label="toggle-theme"
              className={styles.themeToggleButton}
              style={{
                cursor: "pointer",
                marginRight: "12px",
              }}
            >
              {isDark ? (
                <BulbOutlined className={styles.themeIcon} />
              ) : (
                <MoonOutlined className={styles.themeIcon} />
              )}
            </div>
          )}
          {/* 关闭聊天按钮 */}
          <div
            onClick={openCloseChatConfirm}
            role="button"
            aria-label="close-chat"
            className={styles.powerButton}
            style={{
              cursor:
                imageLoading || videoLoading || fileLoading
                  ? "not-allowed"
                  : "pointer",
              opacity: imageLoading || videoLoading || fileLoading ? 0.6 : 1,
            }}
          >
            <PoweroffOutlined className={styles.powerIcon} />
          </div>
        </div>
      </Sender.Header>
    );
  }, [
    shouldShowCloseButton,
    openCloseChatConfirm,
    imageLoading,
    videoLoading,
    fileLoading,
    styles.headerCloseWrapper,
    styles.powerButton,
    styles.powerIcon,
    styles.themeToggleButton,
    styles.themeIcon,
    toggleTheme,
    isDark,
    colors.background,
    externalThemeColors,
  ]);

  // 判断是否显示快捷问题
  const shouldShowQuickQuestions = robotSettings.openQuickQa;

  const filteredQuickQuestions = useMemo(() => {
    // 如果 quickQaIds 为空、未定义、null 或空数组，展示所有快捷问题
    if (!quickQaIds || quickQaIds.length === 0) {
      return quickQuestions || [];
    }
    // 否则只展示在 quickQaIds 中的快捷问题
    // 注意：quickQaIds 是字符串数组，q.id 可能是数字或字符串，需要统一转换为字符串比较
    return (quickQuestions || []).filter(q => 
      quickQaIds.includes(String(q.id))
    );
  }, [quickQuestions, quickQaIds]);

  const quickQuestionTheme = useMemo(
    () =>
      uiVariant === "v2"
        ? quickQuestionThemeFromPageTokens(v2Tokens)
        : quickQuestionThemeFromLegacyColors(colors, isDark),
    [uiVariant, v2Tokens, colors, isDark]
  );

  // ==================== Event ====================
  // 优化提交处理
  const onSubmit = useCallback(
    (nextContent: string) => {
      // 如果聊天已关闭，不允许发送
      if (chatStatus === "close") {
        return;
      }

      // 检查是否有图片、视频、文件或文本内容
      const hasImage = !!uploadedImageUrlRef.current;
      const hasVideo = !!uploadedVideoUrlRef.current;
      const hasFile = !!uploadedFileUrlRef.current;
      const hasText = !!nextContent.trim();

      // 如果既没有文本也没有图片/视频/文件，则不发送
      if (!hasImage && !hasVideo && !hasFile && !hasText) {
        return;
      }

      // 使用onRequest发送消息，当只有媒体文件时传入空格字符串
      onRequest(hasText ? nextContent : " ");

      // 清理状态
      setContent("");
      if (hasImage || hasVideo || hasFile) {
        clearSelectedMedia();
      }
    },
    [
      onRequest,
      clearSelectedMedia,
      chatStatus,
      setContent,
      uploadedImageUrlRef,
      uploadedVideoUrlRef,
      uploadedFileUrlRef,
    ]
  );

  const canSendV2 = useMemo(() => {
    if (uiVariant !== "v2") return false;
    if (imageLoading || videoLoading || fileLoading) return false;
    const hasUploadedMedia = Boolean(
      uploadedImageUrl || uploadedVideoUrl || uploadedFileUrl
    );
    // 上传成功后 setContent(" ")，legacy Sender 用 !innerValue 判断，空格也算可发送
    const hasContent = Boolean(content);
    return hasContent || hasUploadedMedia;
  }, [
    uiVariant,
    content,
    uploadedImageUrl,
    uploadedVideoUrl,
    uploadedFileUrl,
    imageLoading,
    videoLoading,
    fileLoading,
  ]);

  const isSendLoading =
    agent.isRequesting() || imageLoading || videoLoading || fileLoading;

  const onPromptsItemClick: GetProp<typeof Prompts, "onItemClick"> = (info) => {
    onRequest(info.data.description as string);
  };

  // ==================== 带主题的消息渲染 ====================
  const renderLocalMessageWithTheme: BubbleProps["messageRender"] = useCallback(
    (content: any) => {
      // 如果是PDF，使用主题颜色
      if (typeof content === "string" && /\.pdf(\?|#|$)/i.test(content)) {
        return (
          <div
            style={{
              cursor: "pointer",
              maxWidth: "250px",
              marginTop: "8px",
              overflow: "hidden",
              borderRadius: "4px",
              position: "relative",
            }}
            onClick={() => {
              const event = new CustomEvent("preview-file", {
                detail: { url: content },
              });
              window.dispatchEvent(event);
            }}
          >
            <div
              style={{
                position: "relative",
                width: "100%",
                height: "200px",
                backgroundColor: colors.background,
                overflow: "hidden",
                borderRadius: "4px",
                willChange: "transform",
                backfaceVisibility: "hidden",
                transform: "translateZ(0)",
                transition: "opacity 0.2s",
                border: `1px solid ${colors.border}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "0.9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "1";
              }}
            >
              <iframe
                src={`${content}#page=1&zoom=page-width`}
                style={{
                  border: "none",
                  pointerEvents: "none",
                  transform: "scale(0.3)",
                  transformOrigin: "top left",
                  width: "333%",
                  height: "333%",
                }}
                title="PDF Preview"
                loading="lazy"
              />
            </div>
          </div>
        );
      }
      // 其他内容使用默认渲染
      if (renderLocalMessage) {
        return renderLocalMessage(content) || content;
      }
      return content;
    },
    [colors]
  );

  const agentAvatarSrc = robotSettings.agentAvatarUrl?.trim() || aiAvatar;
  const agentAvatarNode = (
    <img
      src={agentAvatarSrc}
      alt="Agent"
      className={uiVariant === "v2" ? "uchat-v2-agent-avatar" : styles.avatarImg}
    />
  );

  // ==================== 角色配置 ====================
  const roles: GetProp<typeof Bubble.List, "roles"> = {
    ai: {
      placement: "start",
      typing: { step: 5, interval: 20 },
      styles: {
        content: {
          borderRadius: uiVariant === "v2" ? 12 : 16,
          ...(v2AgentBubbleStyle ?? {
            backgroundColor: isDark ? "#1e2338" : "#f0f0f0",
            color: colors.text,
          }),
        },
      },
      avatar: agentAvatarNode,
      messageRender: renderLocalMessageWithTheme,
    },
    local: {
      placement: "end",
      variant: uiVariant === "v2" ? "filled" : "shadow",
      styles: {
        content: {
          borderRadius: uiVariant === "v2" ? 12 : 16,
          ...(v2UserBubbleStyle ?? {
            color: colors.text,
          }),
        },
      },
      avatar:
        uiVariant === "v2" ? undefined : (
          <img src={userAvatar} alt="User" className={styles.avatarImg} />
        ),
      messageRender: renderLocalMessageWithTheme,
    },
    suggestion: {
      placement: "start",
      avatar:
        uiVariant === "v2"
          ? { style: { display: "none", width: 0, height: 0, margin: 0, padding: 0 } }
          : { icon: <UserOutlined />, style: { visibility: "hidden" } },
      variant: "borderless",
      messageRender: (items) => (
        <Prompts
          vertical
          items={items as any}
          onItemClick={(info) => {
            onRequest(info.data.description as string);
          }}
        />
      ),
    },
    system: {
      placement: "start",
      variant: "borderless",
      styles: {
        content: {
          width: "100%",
          maxWidth: "100%",
          color: colors.text,
        },
      },
      messageRender: renderLocalMessageWithTheme,
    },
  };

  const items: GetProp<typeof Bubble.List, "items"> = messages;

  // 调试：检查 header 是否被正确设置
  useEffect(() => {
    if (items.length > 0) {
      const firstMessageWithHeader = items.find((item: any) => item.header);
      if (firstMessageWithHeader) {
        console.log("✅ Header 已设置:", firstMessageWithHeader.header);
      } else {
        console.warn("⚠️ 没有找到包含 header 的消息");
      }
    }
  }, [items]);

  // 添加一个ref用于消息容器
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const markReadEnabled =
    chatStatus === "chatting" || chatStatus === "undistributed";

  useMarkReadWhenViewed({
    code,
    uid,
    csUuid,
    containerRef: messagesContainerRef,
    enabled: markReadEnabled,
    messageCount: items.length,
  });

  // 添加滚动到底部的函数
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop =
          messagesContainerRef.current.scrollHeight;
      }
    }, 100); // 100ms 延时确保内容已完全渲染
  }, []);

  // 消息更新时滚动到底部
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // 暴露 fetchChatContent 方法给父组件
  useEffect(() => {
    if (onRef) {
      onRef({ fetchChatContent });
    }
  }, [onRef, fetchChatContent]);

  // 添加图片预览事件监听
  useEffect(() => {
    const handleImagePreview = (event: CustomEvent) => {
      const { url } = event.detail;
      handlePreview(url);
    };

    window.addEventListener(
      "preview-image",
      handleImagePreview as EventListener
    );

    return () => {
      window.removeEventListener(
        "preview-image",
        handleImagePreview as EventListener
      );
    };
  }, [handlePreview]);

  // 添加视频预览事件监听
  useEffect(() => {
    const handleVideoPreviewEvent = (event: CustomEvent) => {
      const { url } = event.detail;
      handleVideoPreview(url);
    };

    window.addEventListener(
      "preview-video",
      handleVideoPreviewEvent as EventListener
    );

    return () => {
      window.removeEventListener(
        "preview-video",
        handleVideoPreviewEvent as EventListener
      );
    };
  }, [handleVideoPreview]);

  // 添加文件预览事件监听
  useEffect(() => {
    const handleFilePreviewEvent = (event: CustomEvent) => {
      const { url } = event.detail;
      handleFilePreview(url);
    };

    window.addEventListener(
      "preview-file",
      handleFilePreviewEvent as EventListener
    );

    return () => {
      window.removeEventListener(
        "preview-file",
        handleFilePreviewEvent as EventListener
      );
    };
  }, [handleFilePreview]);

  // 添加订单提交事件监听
  useEffect(() => {
    const handleOrderSubmit = async (event: Event) => {
      const customEvent = event as CustomEvent<{ orderNo: string }>;
      const { orderNo } = customEvent.detail;
      if (!orderNo) return;

      try {
        // 直接调用 createQaRecord 提交订单号作为用户输入
        await createQaRecord(orderNo, null, null, null);
      } catch (error) {
        console.error("Error submitting order:", error);
        antMessage.error(intl.formatMessage({ id: "message.send.error" }));
      }
    };

    window.addEventListener("submit-order", handleOrderSubmit);

    return () => {
      window.removeEventListener("submit-order", handleOrderSubmit);
    };
  }, [createQaRecord, intl]);

  return (
    <div
      className={`${styles.layout} ${styles.layoutRelative}${
        uiVariant === "v2" ? " uchat-v2-chat-root" : ""
      }`}
    >
      {/* 🌟 隐藏的文件输入器 - 移到根级别 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*,.pdf,application/pdf"
        className={styles.hiddenFileInput}
        onChange={handleFileSelect}
        disabled={imageLoading || videoLoading}
      />

      <div
        className={styles.chat}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {/* 客服正在输入：预留固定高度，避免突然插入导致布局跳动 */}
        {typingHintEnabled ? (
          <div
            className={`${styles.typingHint}${
              showAgentTyping ? "" : ` ${styles.typingHintIdle}`
            }${uiVariant === "v2" ? " uchat-v2-typing-hint" : ""}`}
            aria-live="polite"
            aria-hidden={!showAgentTyping}
          >
            <span className={styles.typingHintText}>
              {intl.formatMessage({ id: "chat.agent.typing" })}
            </span>
            <span className={styles.typingDots} aria-hidden>
              <i />
              <i />
              <i />
            </span>
          </div>
        ) : null}

        {/* 添加 ref 到消息列表容器 */}
        <div
          ref={messagesContainerRef}
          className={`${styles.messages}${uiVariant === "v2" ? " uchat-v2-messages" : ""}`}
        >
          <Bubble.List
            items={items.length > 0 ? items : [{ variant: "borderless" }]}
            roles={roles}
          />
        </div>

        {/* 图片预览区域 */}
        <div
          className={`${styles.imagePreviewRow}${
            uiVariant === "v2" ? " uchat-v2-attachment-preview" : ""
          }`}
          style={{
            transform: `scale(${previewBase64 ? 1 : 0})`,
            height: previewBase64 ? "48px" : "0px",
            opacity: previewBase64 ? 1 : 0,
          }}
        >
          {previewBase64 && (
            <ImagePreview
              src={previewBase64}
              onPreview={() => handlePreview(previewBase64)}
              onRemove={(e) => {
                e.stopPropagation();
                clearSelectedMedia();
              }}
              loading={!uploadedImageUrl}
              styles={styles}
            />
          )}
        </div>

        {/* 视频预览区域 */}
        <div
          className={`${styles.imagePreviewRow}${
            uiVariant === "v2" ? " uchat-v2-attachment-preview" : ""
          }`}
          style={{
            transform: `scale(${previewVideo ? 1 : 0})`,
            height: previewVideo ? "48px" : "0px",
            opacity: previewVideo ? 1 : 0,
          }}
        >
          {previewVideo && (
            <VideoPreview
              src={previewVideo}
              onPreview={() => handleVideoPreview(uploadedVideoUrl || "")}
              onRemove={(e) => {
                e.stopPropagation();
                clearSelectedMedia();
              }}
              loading={!uploadedVideoUrl}
              styles={styles}
            />
          )}
        </div>

        {/* 文件预览区域 */}
        <div
          className={`${styles.imagePreviewRow}${
            uiVariant === "v2" ? " uchat-v2-attachment-preview" : ""
          }`}
          style={{
            transform: `scale(${previewFile ? 1 : 0})`,
            height: previewFile ? "48px" : "0px",
            opacity: previewFile ? 1 : 0,
          }}
        >
          {previewFile && (
            <FilePreview
              fileName={previewFile.name}
              onPreview={() => handleFilePreview(uploadedFileUrl || "")}
              onRemove={(e) => {
                e.stopPropagation();
                clearSelectedMedia();
              }}
              loading={!uploadedFileUrl}
              styles={styles}
            />
          )}
        </div>

        {/* 图片预览模态框 */}
        <Modal
          open={previewVisible}
          footer={null}
          onCancel={handleCancel}
          width={800}
          className={styles.imagePreviewModal}
          centered
          styles={{
            content: {
              backgroundColor: colors.background,
            },
            header: {
              backgroundColor: colors.background,
            },
          }}
        >
          <img
            alt="preview"
            src={previewImage}
            className={styles.modalMediaFull}
            onClick={handleCancel}
            style={{ cursor: "pointer" }}
          />
        </Modal>

        {/* 视频播放模态框 */}
        <Modal
          open={videoVisible}
          footer={null}
          onCancel={handleVideoCancel}
          width={800}
          className={styles.imagePreviewModal}
          centered
          styles={{
            content: {
              backgroundColor: colors.background,
            },
            header: {
              backgroundColor: colors.background,
            },
          }}
        >
          <video
            controls
            className={styles.modalMediaFull}
            src={currentVideo}
          />
        </Modal>

        {/* 文件预览模态框 */}
        <Modal
          open={fileVisible}
          footer={null}
          onCancel={handleFileCancel}
          width={800}
          className={styles.imagePreviewModal}
          centered
          title={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span>{intl.formatMessage({ id: "pdf.preview.title" })}</span>
              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={async () => {
                    if (currentFile) {
                      // 从PDF查看器URL中提取实际PDF下载地址
                      const getActualPdfUrl = (url: string): string => {
                        try {
                          // 检查是否是PDF查看器URL（包含 pdf= 参数）
                          if (url.includes("pdf=")) {
                            const match = url.match(/[?&]pdf=([^&]+)/i);
                            if (match) {
                              return decodeURIComponent(match[1]);
                            }
                          }
                          return url;
                        } catch {
                          return url;
                        }
                      };

                      const actualPdfUrl = getActualPdfUrl(currentFile);

                      try {
                        // 使用 fetch 下载文件，支持跨域
                        const response = await fetch(actualPdfUrl);
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.download =
                          actualPdfUrl.split("/").pop() || "document.pdf";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                      } catch (error) {
                        // 如果 fetch 失败，回退到直接打开链接
                        const link = document.createElement("a");
                        link.href = actualPdfUrl;
                        link.download =
                          actualPdfUrl.split("/").pop() || "document.pdf";
                        link.target = "_blank";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    }
                  }}
                  style={{ padding: 0 }}
                >
                  {intl.formatMessage({ id: "pdf.preview.download" })}
                </Button>
                <Button
                  type="link"
                  icon={<ExportOutlined />}
                  onClick={() => {
                    if (currentFile) {
                      // 从PDF查看器URL中提取实际PDF地址，如果存在则使用，否则使用原始URL
                      let urlToOpen = currentFile;
                      if (currentFile.includes("pdf=")) {
                        const match = currentFile.match(/[?&]pdf=([^&]+)/i);
                        if (match) {
                          urlToOpen = decodeURIComponent(match[1]);
                        }
                      }
                      window.open(urlToOpen, "_blank", "noopener,noreferrer");
                    }
                  }}
                  style={{ padding: 0 }}
                >
                  {intl.formatMessage({ id: "pdf.preview.open.new.tab" })}
                </Button>
              </div>
            </div>
          }
          styles={{
            content: {
              backgroundColor: colors.background,
            },
            header: {
              backgroundColor: colors.background,
            },
          }}
        >
          <div
            style={{
              width: "100%",
              height: "600px",
              overflow: "auto",
              position: "relative",
              backgroundColor: colors.background,
            }}
          >
            <iframe
              src={currentFile || ""}
              style={{
                border: "none",
                transform: "scale(0.8)",
                transformOrigin: "top left",
                width: "125%",
                height: "125%",
              }}
              title="File Preview"
              allowFullScreen
            />
          </div>
        </Modal>

        {/* 🌟 提示词 */}
        <Prompts items={senderPromptsItems} onItemClick={onPromptsItemClick} />

        {/* 🌟 快捷问题浮窗 - 用户点击后立即显示问答 */}
        {shouldShowQuickQuestions && (
          <QuickQuestionFloating
            questions={filteredQuickQuestions}
            onQuestionClick={handleQuickQuestionClick}
            disabled={imageLoading || videoLoading || fileLoading}
            defaultExpanded={robotSettings.openQuickQa || false}
            theme={quickQuestionTheme}
          />
        )}

        {/* 🌟 输入框 */}
        <div
          className={uiVariant === "v2" ? "uchat-v2-sender-wrap" : undefined}
          style={{ width: "100%" }}
        >
          {uiVariant === "v2" && shouldShowCloseButton ? (
            <V2CloseSessionButton
              onClick={openCloseChatConfirm}
              disabled={imageLoading || videoLoading || fileLoading}
              title={intl.formatMessage({
                id: "close.button.title.normal",
              })}
              aria-label={intl.formatMessage({
                id: "close.chat.confirm.title",
              })}
            />
          ) : null}
          {uiVariant === "v2" ? (
            <Sender
              header={undefined}
              value={content}
              onSubmit={onSubmit}
              onChange={setContent}
              loading={
                agent.isRequesting() || imageLoading || videoLoading
              }
              disabled={
                getSenderProps().disabled || imageLoading || videoLoading
              }
              placeholder={intl.formatMessage({
                id: "v2.type_message",
                defaultMessage: "Type a message…",
              })}
              className="uchat-v2-sender"
              actions={
                <div className="uchat-v2-sender-action-group">
                  <Upload
                    accept="image/*,video/*,.pdf,application/pdf"
                    showUploadList={false}
                    beforeUpload={handleGeneralFileUpload}
                    disabled={imageLoading || videoLoading || fileLoading}
                  >
                    <button
                      type="button"
                      className="uchat-v2-upload-btn"
                      title={intl.formatMessage({
                        id: "v2.upload_media",
                        defaultMessage: "Upload image, video or PDF",
                      })}
                      aria-label={intl.formatMessage({
                        id: "v2.upload_media",
                        defaultMessage: "Upload image, video or PDF",
                      })}
                    >
                      <V2AttachmentIcon size={24} />
                    </button>
                  </Upload>
                  <button
                    type="button"
                    className={`uchat-v2-send-btn${
                      !isSendLoading ? " uchat-v2-send-btn--bright" : ""
                    }`}
                    disabled={
                      !canSendV2 ||
                      isSendLoading ||
                      getSenderProps().disabled
                    }
                    onClick={() => onSubmit(content)}
                    aria-label={intl.formatMessage({
                      id: "message.send",
                      defaultMessage: "Send",
                    })}
                  >
                    {isSendLoading ? (
                      <LoadingOutlined />
                    ) : (
                      <V2SendIcon size={28} />
                    )}
                  </button>
                </div>
              }
            />
          ) : (
            <>
              <Sender
                header={headerNode}
                value={content}
                onSubmit={onSubmit}
                onChange={setContent}
                loading={agent.isRequesting() || imageLoading || videoLoading}
                disabled={
                  getSenderProps().disabled || imageLoading || videoLoading
                }
                placeholder={getSenderProps().placeholder}
                className={styles.sender}
                classNames={{ actions: styles.senderActions }}
              />

              {/* 🌟 输入框下方的上传工具栏 */}
              <Flex
                align="center"
                style={{
                  width: "92%",
                  margin: "-12px auto 0 auto",
                  display: "flex",
                }}
              >
            {/* 左：主要上传按钮 - 支持图片和视频 */}
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <Upload
                accept="image/*,video/*,.pdf,application/pdf"
                showUploadList={false}
                beforeUpload={handleGeneralFileUpload}
                disabled={imageLoading || videoLoading || fileLoading}
              >
                <div
                  className={styles.senderToolbarItem}
                  style={{
                    cursor:
                      imageLoading || videoLoading ? "not-allowed" : "pointer",
                    opacity: imageLoading || videoLoading ? 0.6 : 1,
                  }}
                >
                  <PlusOutlined />
                </div>
              </Upload>
            </div>
            {/* 中：快捷上传按钮 - 图片 */}
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleImageUpload}
                disabled={imageLoading || videoLoading || fileLoading}
              >
                <div
                  className={styles.senderToolbarItem}
                  style={{
                    cursor:
                      imageLoading || videoLoading ? "not-allowed" : "pointer",
                    opacity: imageLoading || videoLoading ? 0.6 : 1,
                  }}
                >
                  <PictureOutlined />
                </div>
              </Upload>
            </div>
            {/* 右：快捷上传按钮 - 视频 */}
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Upload
                accept="video/*"
                showUploadList={false}
                beforeUpload={handleVideoUpload}
                disabled={imageLoading || videoLoading || fileLoading}
              >
                <div
                  className={styles.senderToolbarItem}
                  style={{
                    cursor:
                      imageLoading || videoLoading ? "not-allowed" : "pointer",
                    opacity: imageLoading || videoLoading ? 0.6 : 1,
                  }}
                >
                  <VideoCameraOutlined />
                </div>
              </Upload>
            </div>
          </Flex>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default IndependentChat;
